import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Spinner } from './feedback.tsx';
import { Tooltip } from './overlay.tsx';

export const buttonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap select-none',
    'transition-colors duration-fast [&_svg]:shrink-0',
    'disabled:cursor-not-allowed aria-busy:cursor-progress',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-fg hover:bg-primary-hover disabled:bg-surface-muted disabled:text-fg-disabled',
        secondary:
          'border border-strong bg-surface text-fg shadow-xs hover:bg-surface-muted disabled:border-subtle disabled:text-fg-disabled',
        ghost: 'text-fg hover:bg-surface-muted disabled:text-fg-disabled',
        danger:
          'bg-danger-solid text-danger-solid-fg hover:bg-danger-solid-hover disabled:bg-surface-muted disabled:text-fg-disabled',
        link: 'text-link underline-offset-4 hover:underline disabled:text-fg-disabled',
      },
      size: {
        // Mobilde dokunma hedefi 44 px (08 İ-4; --control-h ile aynı kural), masaüstünde sıkı 32 px.
        sm: 'h-11 px-3 text-body-sm md:h-8',
        md: 'h-control px-4 text-body',
        lg: 'h-12 px-5 text-body',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto min-h-6 px-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Kendi öğesini (ör. bağlantı) düğme gibi biçimler. */
    asChild?: boolean;
    /** İşlem sürerken: düğme devre dışı kalır, `aria-busy` taşır ve dönen gösterge görünür. */
    loading?: boolean;
    icon?: ReactNode;
  };

/**
 * Düğme (08 §6 #10). Metin fiille başlar ve sonucu söyler ("İzni onayla"); "Tamam" ve "Gönder" tek başına
 * kullanılmaz (08 §9.2). Sayfa başlığında en fazla bir `primary` bulunur.
 */
export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  icon,
  disabled,
  children,
  type,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner size={16} className="text-current" /> : icon}
      {children}
    </button>
  );
}

type IconButtonProps = Omit<ComponentProps<'button'>, 'aria-label' | 'children'> &
  Pick<VariantProps<typeof buttonVariants>, 'variant'> & {
    /** Zorunlu: erişilebilir ad ve araç ipucu aynı metindir (08 §5.6). */
    label: string;
    icon: ReactNode;
    size?: 'sm' | 'md';
    loading?: boolean;
  };

/** Yalnız ikonlu düğme: `aria-label` ve araç ipucu zorunludur; ikon tek başına eylem anlatmaz. */
export function IconButton({ label, icon, variant = 'ghost', size = 'md', loading = false, className, disabled, type, ...props }: IconButtonProps) {
  return (
    <Tooltip content={label}>
      <button
        type={type ?? 'button'}
        aria-label={label}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant }), size === 'sm' ? 'size-11 px-0 md:size-8' : 'size-control px-0', className)}
        {...props}
      >
        {loading ? <Spinner size={16} className="text-current" /> : icon}
      </button>
    </Tooltip>
  );
}
